import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';

// Global Components
import Header from './components/Header';
import Footer from './components/Footer';
import BackgroundFX from './components/BackgroundFX';

// Pages
import Home from './pages/Home';
import About from './pages/About';
import Services from './pages/Services';
import Articles from './pages/Articles';
import Contact from './pages/Contact';
import FAQs from './pages/FAQs';
import SingleArticle from './pages/SingleArticle';
import SingleService from './pages/SingleService';
import NotFound from './pages/404';
import Career from './pages/Career';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsConditions from './pages/TermsConditions';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import Academy from './pages/Academy';
import Work from './pages/Work';

// Services Pages
import ServiceCustomSoftware from './pages/ServiceCustomSoftware';
import ServiceCMS from './pages/ServiceCMS';
import ServiceWorkflowAutomation from './pages/ServiceWorkflowAutomation';
import ServiceGenerativeAI from './pages/ServiceGenerativeAI';
import ServiceAISystems from './pages/ServiceAISystems';
import ServiceSaaSEngineering from './pages/ServiceSaaSEngineering';

// Case Studies (new, locked-down set)
import CaseStudiesPage from './features/caseStudies/CaseStudiesPage';

// Individual Articles
import AutomationWorkflowsArticle from './pages/articles/AutomationWorkflows';
import DesigningAISystemsArticle from './pages/articles/DesigningAISystems';
import GenerativeAIProductionArticle from './pages/articles/GenerativeAIProduction';
import CMS2026Article from './pages/articles/CMS2026';
import SaaSArchitectureArticle from './pages/articles/SaaSArchitecture';
import SoftwareThatLastsArticle from './pages/articles/SoftwareThatLasts';
import AIAutomationArticle from './pages/articles/AIAutomation';
import EnterpriseAppDevArticle from './pages/articles/EnterpriseAppDev';
import DataAsAProductArticle from './pages/articles/DataAsAProduct';
import AIReadinessArticle from './pages/articles/AIReadiness';
import APIFirstDevelopmentArticle from './pages/articles/APIFirstDevelopment';
import SecurityByDesignArticle from './pages/articles/SecurityByDesign';
import AutomationROIArticle from './pages/articles/AutomationROI';
import ChoosingTechStackArticle from './pages/articles/ChoosingTechStack';
import FutureOfITConsultingArticle from './pages/articles/FutureOfITConsulting';

function App() {
  return (
    <Router>
      <BackgroundFX />
      <div className="app-shell">
        <Header />
        <AnimatedRoutes />
        <Footer />
      </div>
    </Router>
  );
}

export default App;

const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <motion.main
        id="app-content"
        key={location.pathname}
        className="page-transition"
        initial={{ opacity: 0, y: 18, filter: 'blur(6px)' }}
        animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
        exit={{ opacity: 0, y: -18, filter: 'blur(6px)' }}
        transition={{ duration: 0.5, ease: [0.22, 0.61, 0.36, 1] }}
      >
        {/* MATCH URL → RENDER THE PAGE */}
        <Routes location={location}>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/services" element={<Services />} />
          <Route path="/products" element={<Products />} />
          <Route path="/products/:productId" element={<ProductDetail />} />
          <Route path="/academy" element={<Academy />} />
          <Route path="/work" element={<Work />} />
          <Route path="/projects" element={<Work />} />
          <Route path="/articles" element={<Articles />} />
          <Route path="/case-studies" element={<CaseStudiesPage />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/faqs" element={<FAQs />} />
          <Route path="/career" element={<Career />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-and-conditions" element={<TermsConditions />} />
          {/* Services Detail Pages */}
          <Route
            path="/services/custom-software-enterprise-applications"
            element={<ServiceCustomSoftware />}
          />
          <Route path="/services/cms-digital-experience-platforms" element={<ServiceCMS />} />
          <Route
            path="/services/workflow-automation-system-integrations"
            element={<ServiceWorkflowAutomation />}
          />
          <Route path="/services/generative-ai-solutions" element={<ServiceGenerativeAI />} />
          <Route path="/services/ai-systems-integrations" element={<ServiceAISystems />} />
          <Route path="/services/saas-product-engineering" element={<ServiceSaaSEngineering />} />
          {/* Article Detail Pages */}
          <Route path="/articles/automation-workflows-scalable-operations" element={<AutomationWorkflowsArticle />} />
          <Route path="/articles/designing-ai-systems-business-problems" element={<DesigningAISystemsArticle />} />
          <Route path="/articles/generative-ai-in-production" element={<GenerativeAIProductionArticle />} />
          <Route path="/articles/cms-2026-content-architecture" element={<CMS2026Article />} />
          <Route path="/articles/saas-architecture-explained" element={<SaaSArchitectureArticle />} />
          <Route path="/articles/building-software-that-lasts" element={<SoftwareThatLastsArticle />} />
          <Route path="/articles/ai-plus-automation" element={<AIAutomationArticle />} />
          <Route path="/articles/enterprise-application-development" element={<EnterpriseAppDevArticle />} />
          <Route path="/articles/data-as-a-product" element={<DataAsAProductArticle />} />
          <Route path="/articles/ai-readiness-assessment" element={<AIReadinessArticle />} />
          <Route path="/articles/api-first-development" element={<APIFirstDevelopmentArticle />} />
          <Route path="/articles/security-by-design" element={<SecurityByDesignArticle />} />
          <Route path="/articles/automation-roi" element={<AutomationROIArticle />} />
          <Route path="/articles/choosing-right-tech-stack" element={<ChoosingTechStackArticle />} />
          <Route path="/articles/future-of-it-consulting" element={<FutureOfITConsultingArticle />} />
          <Route path="/articles/:id" element={<SingleArticle />} />
          {/* Dynamic (/:id) Detail Pages */}
          <Route path="/article/:id" element={<SingleArticle />} />
          <Route path="/service/:id" element={<SingleService />} />
          {/* 404 Fallback */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </motion.main>
    </AnimatePresence>
  );
};


